import React, { useState } from 'react';
import { cartOrderPublicClient } from '../../services/cart-order/client';
import { HttpStatusCode } from 'axios';
import { Alert, Button, Grid, Snackbar, TextField } from '@mui/material';

type IneligibleDiscountReasons = {
  ecommOnly?: true;
  invalid?: true;
  expired?: true;
  ineligibleEmail?: true;
};

const DISCOUNT_ERROR_MESSAGES: Record<keyof IneligibleDiscountReasons, string> =
  {
    ecommOnly: 'Discount is not valid for programs',
    invalid: 'Invalid discount',
    expired: 'Expired discount',
    ineligibleEmail: 'Email address is ineligible for discount',
  };

const DiscountForm = ({
  programId,
  email,
  setAppliedDiscountCode,
  setDiscountedGrandTotalInCents,
}: {
  programId: number;
  email?: string;
  setAppliedDiscountCode: (discountCode?: string) => void;
  setDiscountedGrandTotalInCents: (grandTotal: number) => void;
}) => {
  const [discountCode, setDiscountCode] = useState<string | undefined>();
  const [discountDescription, setDiscountDescription] = useState<
    string | undefined
  >();

  const [discountValidationIsLoading, setDiscountValidationIsLoading] =
    useState(false);

  const enableButton =
    !!discountCode && !!email && !discountValidationIsLoading;
  const missingEmail = !!discountCode && !email;

  const [apiDiscountError, setApiDiscountError] = useState<
    IneligibleDiscountReasons | undefined
  >();
  const eligibilityError = Object.keys(apiDiscountError || {}).find(
    (key) => apiDiscountError?.[key as keyof IneligibleDiscountReasons],
  );
  const eligibilityErrorMessage = eligibilityError
    ? DISCOUNT_ERROR_MESSAGES[
        eligibilityError as keyof IneligibleDiscountReasons
      ]
    : undefined;

  const errorHelperText = missingEmail
    ? 'Valid email required to apply discount code'
    : eligibilityErrorMessage;

  const helperTextIsError = !!errorHelperText;

  const applyDiscountCode = async (code: string) => {
    setDiscountValidationIsLoading(true);
    const response = await cartOrderPublicClient(1).post(
      '/programs/calculate-price',
      {
        programId,
        discountCode: code,
        email,
      },
      {
        validateStatus: (status) =>
          [HttpStatusCode.Ok, HttpStatusCode.BadRequest].includes(status),
      },
    );
    switch (response.status) {
      case HttpStatusCode.Ok:
        setAppliedDiscountCode(code);
        setDiscountedGrandTotalInCents(
          response.data.grandTotalInCents as number,
        );
        setDiscountDescription(response.data.discountDetails.publicDescription);
        setDiscountSnackbarIsOpen(true);
        setApiDiscountError(undefined);
        break;
      case HttpStatusCode.BadRequest:
        setApiDiscountError(
          response.data['discountDetails']?.['eligibilityResult'],
        );
    }

    setDiscountValidationIsLoading(false);
  };

  const [discountSnackbarIsOpen, setDiscountSnackbarIsOpen] = useState(false);

  return (
    <>
      <Snackbar
        open={discountSnackbarIsOpen && !!discountDescription}
        anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
        sx={{ marginTop: '50px' }}
        autoHideDuration={6000}
        onClose={() => setDiscountSnackbarIsOpen(false)}
      >
        <Alert severity={'success'}>{discountDescription}</Alert>
      </Snackbar>
      <Grid container columnSpacing={2}>
        <Grid size={8}>
          <TextField
            color={'primary'}
            size={'small'}
            fullWidth
            label={'Discount Code'}
            value={discountCode}
            onChange={(event) => setDiscountCode(event.target.value)}
            error={helperTextIsError}
            helperText={errorHelperText}
          />
        </Grid>
        <Grid size={4}>
          <Button
            color={'primary'}
            fullWidth
            variant={'contained'}
            size={'medium'}
            disabled={!enableButton}
            onClick={() => applyDiscountCode(discountCode!)}
          >
            Apply
          </Button>
        </Grid>
      </Grid>
    </>
  );
};

export default DiscountForm;
